import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, normalizeStyle as _normalizeStyle, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, createVNode as _createVNode, toDisplayString as _toDisplayString, withCtx as _withCtx, createElementBlock as _createElementBlock, withModifiers as _withModifiers, renderList as _renderList, Fragment as _Fragment, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = {
  key: 1,
  class: "create-edit-job__header"
}
const _hoisted_2 = { class: "create-edit-job__header-title" }
const _hoisted_3 = { class: "create-edit-job__body" }
const _hoisted_4 = { class: "create-edit-job__body-options" }
const _hoisted_5 = { class: "create-edit-job__body-options-checkbox" }
const _hoisted_6 = { class: "create-edit-job__body-options-checkbox-label" }
const _hoisted_7 = { class: "create-edit-job__body-files-block" }
const _hoisted_8 = {
  key: 0,
  class: "create-edit-job__body-files"
}
const _hoisted_9 = ["onClick"]
const _hoisted_10 = { class: "create-edit-job__body-files-item-name" }
const _hoisted_11 = { class: "create-edit-job__body-files-item-name" }
const _hoisted_12 = { class: "create-edit-job__body-tags-block" }
const _hoisted_13 = { class: "create-edit-job__footer" }

import {
	computed,
	nextTick,
	onMounted,
	onUnmounted,
	ref,
	watch
} from "vue";
import AppTextarea from "@/components/ui-kit/textarea/AppTextarea.vue";
import AppIcon from "@/components/ui-kit/icon/AppIcon.vue";
import AppLoader from "@/components/common/AppLoader.vue";
import AppSelect from "@/components/ui-kit/select/AppSelect.vue";
import type { SelectItem, SelectModelValue } from "@/components/ui-kit/select/AppSelect.vue";
import AppCheckbox from "@/components/ui-kit/checkbox/AppCheckbox.vue";
import AppButton from "@/components/ui-kit/button/AppButton.vue";
import AppDateString from "@/components/ui-kit/datepicker/AppDateString.vue";
import AppTooltip from "@/components/ui-kit/tooltip/AppTooltip.vue";
import AppResizableDropdown from "@/components/ui-kit/dropdown/AppResizableDropdown.vue";
import AppTagSelector from "@/components/ui-kit/tag-selector/AppTagSelector.vue";
import ConfirmActionDropdown from "@/components/main/clients-panel/dropdowns/ConfirmActionDropdown.vue";
import FileInfoModal from "@/components/file/modals/FileInfoModal.vue";
import {
	Job,
	JobFile,
	NewJob,
	jobsStore
} from "@/store/jobs";
import {JobsApi, Template} from "@/ api/JobsApi";
import {FileApi} from "@/ api/FileApi";
import {workerStore} from "@/store/worker";
import { useFileDrop, MAX_FILES_PER_BATCH } from "@/composables/useFileDrop";
import { useNotification } from "@/composables/useNotification";
import { useUploadQueue } from "@/composables/useUploadQueue";
import { useBreakpoints } from "@/composables/useBreakpoints";
import { trimTrailingLineBreaks } from "@/utils/textHelpers";
import { findWorkerOption } from "@/utils/jobWorkerOptions";
import { i18n, tp } from "@/i18n";
import { TagBaseType, TagEntityType } from "@/models/etc/types";
import type { TagWithFrequency } from "@/models/interfaces/tags/tag-models";
import { useRoute, useRouter } from "vue-router";

type CreateEditContactProps = {
	job?: Job,
	workers: any[]
	// Строки, закреплённые над списком исполнителей (текущий пользователь)
	pinnedWorkers?: any[]
	type?: 'create' | 'edit'
}

type JobFormState = {
	WorkerIds: number[] | null
	NeedChecking: boolean
	ToDoDate: Date | null
	Message: string
	Tags: TagWithFrequency[]
}

type PendingProjectFile = {
	Id: number
	Name: string
	Signed: boolean
}

const MAX_SIZE_MB = 100

const CONFIRM_WIDTH = 232
const CONFIRM_HEIGHT = 140
const VIEWPORT_GAP = 8

// Окно подтверждения лежит в DOM внутри выпадашки селекта (иначе клик по нему
// закроет селект через v-click-outside), но позиционируется fixed — поэтому
// координаты считаем от иконки и сами удерживаем окно в пределах экрана

export default /*@__PURE__*/_defineComponent({
  __name: 'CreateEditJobDropdown',
  props: {
    job: {},
    workers: {},
    pinnedWorkers: {},
    type: { default: 'create' }
  },
  emits: ["on-close"],
  setup(__props: any, { emit: __emit }) {

const emit = __emit

const props = __props

const { isMobile, isTablet } = useBreakpoints()

const t = (key: string, named?: Record<string, unknown>): string => {
	return String(named ? i18n.global.t(key, named) : i18n.global.t(key))
}

const store = jobsStore()
const userStore = workerStore()
const route = useRoute()
const router = useRouter()
const notify = useNotification()
const uploadQueue = useUploadQueue()

const isLoading = ref(false)
const templates = ref<Template[]>([])
const showTemplates = ref(false)
const selectedTemplate = ref<number | null>(null)
const searchedTemplate = ref<string | null>(null)
const templateToDelete = ref<{ id: number, label: string } | null>(null)
const templateConfirmStyle = ref({ top: '0px', left: '0px' })
const isTemplateDeleting = ref(false)

const filters = ref<JobFormState>({
	WorkerIds: null,
	NeedChecking: false,
	ToDoDate: null,
	Message: '',
	Tags: []
})
// Исполнителей в черновике уже меняли — после этого подстановка и снятие текущего
// пользователя больше не срабатывают. Хранится в черновике вместе с filters
const workersTouched = ref(false)

const $uploadFile = ref<HTMLInputElement | null>(null)
const $tagsDropdown = ref<InstanceType<typeof AppResizableDropdown> | null>(null)
const $templatesSelect = ref<InstanceType<typeof AppSelect> | null>(null)
const $fileInfoModal = ref<typeof FileInfoModal | null>(null)
const $messageTextarea = ref<InstanceType<typeof AppTextarea> | null>(null)

const pendingFiles = ref<File[]>([])
const pendingProjectFiles = ref<PendingProjectFile[]>([])

const {
	isDraggingOver,
	canDrop,
	validateFile,
	handleDragOver,
	handleDragEnter,
	handleDragLeave,
	handleDrop,
} = useFileDrop({
	maxSizeMb: MAX_SIZE_MB,
	maxFiles: MAX_FILES_PER_BATCH,
	multiple: true,
	onError: (message) => notify.danger(t('files_label'), message),
	onFiles: async (files) => {
		if (props.type === 'create') {
			for (const file of files) pendingFiles.value.push(file)
			return
		}
		if (props.job) enqueueDeviceUpload(files, props.job.Id)
	},
	onProjectFiles: async (files) => {
		if (props.type === 'create') {
			for (const f of files) {
				pendingProjectFiles.value.push({ Id: f.Id, Name: f.Name, Signed: f.Signed })
			}
			return
		}
		if (props.job) await attachProjectFilesToJob(files, props.job.Id)
	},
})

const currentUser = computed(() => userStore.workerProfile)

// Id текущего пользователя берём только из списка работников, как и закреплённую строку
// в селекте: иначе в WorkerIds уедет id, которого среди items нет
const currentWorkerId = computed(
	() => findWorkerOption(store.jobWorkers, currentUser.value?.Id)?.id ?? null
)

const mappedTemplates = computed(() => templates.value.map(template => ({
	id: template.Id,
	label: template.Name,
	type: 'item'
})))

const existingFiles = computed<JobFile[]>(() => {
	if (props.type !== 'edit' || !props.job?.Files) {
		return []
	}

	return props.job.Files
})

const hasAnyFiles = computed(() =>
	existingFiles.value.length > 0
	|| pendingFiles.value.length > 0
	|| pendingProjectFiles.value.length > 0
)

const fileLocationTarget = computed(() => {
	if (props.type !== 'edit' || !props.job) {
		return null
	}
	if (props.job.Project) {
		return { page: 'project' as const, id: props.job.Project.Id }
	}
	const firstClientId = props.job.Clients?.[0]?.Id
	if (firstClientId && firstClientId !== 1) {
		return { page: 'client' as const, id: firstClientId }
	}

	return null
})

const enqueueDeviceUpload = (files: File[], jobId: number) => {
	uploadQueue.enqueue(files, (file, hooks) =>
		store.attachJobFileFromDevice(file, jobId, hooks)
	)
}

const notifyAttachResult = (ok: number, fail: number) => {
	if (ok > 0 && fail === 0) {
		notify.success(t('files_label'), tp('file_attached_count', ok))
	} else if (ok > 0 && fail > 0) {
		notify.warning(t('files_label'), t('file_attach_partial', { ok, fail }))
	} else if (fail > 0) {
		notify.danger(t('files_label'), tp('file_attach_failed_count', fail))
	}
}

const attachProjectFilesToJob = async (metas: PendingProjectFile[], jobId: number) => {
	let ok = 0
	let fail = 0
	for (const m of metas) {
		try {
			await store.attachJobFileFromClientOrProject(jobId, m.Id, {
				Id: m.Id,
				Name: m.Name,
				Signed: m.Signed,
			})
			ok++
		} catch (e) {
			console.error(e)
			fail++
		}
	}
	notifyAttachResult(ok, fail)
}

const triggerFileInput = () => {
	$uploadFile.value?.click()
}

const handleFileInput = async (event: Event) => {
	const target = event.target as HTMLInputElement
	if (!target.files || target.files.length === 0) {
		return
	}

	const accepted: File[] = []
	for (const file of Array.from(target.files)) {
		const validation = validateFile(file)
		if (!validation.isValid) {
			notify.danger(t('files_label'), validation.error!)
			continue
		}
		accepted.push(file)
	}
	target.value = ''
	if (accepted.length === 0) return

	let files = accepted
	if (files.length > MAX_FILES_PER_BATCH) {
		files = files.slice(0, MAX_FILES_PER_BATCH)
		notify.warning(t('files_label'), t('file_batch_limit', { count: MAX_FILES_PER_BATCH }))
	}
	if (props.type === 'create') {
		for (const file of files) {
			pendingFiles.value.push(file)
		}
		return
	}
	if (props.job) {
		enqueueDeviceUpload(files, props.job.Id)
	}
}

const removePendingFile = (index: number) => {
	pendingFiles.value.splice(index, 1)
}

const removePendingProjectFile = (index: number) => {
	pendingProjectFiles.value.splice(index, 1)
}

const detachExistingFile = async (file: JobFile) => {
	if (!props.job) return

	try {
		isLoading.value = true
		await store.detachJobFile(props.job.Id, file.Id)
	} catch (e) {
		console.error(e)
	} finally {
		isLoading.value = false
	}
}

const showFileInfo = async (file: JobFile) => {
	try {
		isLoading.value = true
		const containerFile = await FileApi.GetFileInfo(file.Id)
		if (containerFile) {
			$fileInfoModal.value?.onOpen(containerFile, "file")
		}
	} catch (e) {
		console.error(e)
	} finally {
		isLoading.value = false
	}
}

const downloadFile = (fileId: number) => {
	try {
		FileApi.FileDownload(fileId)
	} catch (e) {
		console.error(e)
	}
}

const goToFileLocation = () => {
	if (!fileLocationTarget.value) return

	router.push({
		query: {
			...route.query,
			page: fileLocationTarget.value.page,
			id: fileLocationTarget.value.id
		}
	})
}

const onTagSelectorChange = (nextTags: TagWithFrequency[]) => {
	filters.value.Tags = nextTags
}

// Работа ещё может не существовать (создание), а в редактировании теги уходят
// одним пакетом по «Сохранить», поэтому новый тег кладём только в локальный стейт:
// на бэке он создастся по имени
const onCreateTag = (name: string) => {
	const normalized = name.trim().toLowerCase()
	if (!normalized) {
		return
	}
	const current = filters.value.Tags ?? []
	if (current.some(tag => tag.Name.toLowerCase().trim() === normalized)) {
		return
	}
	const tempId = Math.min(0, ...current.map(tag => tag.Id)) - 1
	filters.value.Tags = [...current, {
		Id: tempId,
		Name: normalized,
		Type: TagBaseType.Common,
		Frequency: 0,
		FullName: normalized,
	}]
}

const removeTag = (tag: TagWithFrequency) => {
	filters.value.Tags = filters.value.Tags.filter(t =>
		t.Id !== tag.Id || t.Name.toLowerCase() !== tag.Name.toLowerCase()
	)
}

let toggledGroupIds: number[] | null = null

const onWorkersGroupToggle = (childIds: (string | number)[]) => {
	toggledGroupIds = childIds as number[]
}

// Если к подставленному по умолчанию текущему пользователю добавили кого-то ещё —
// скорее всего работу ставят на другого, поэтому снимаем выбор с себя. Исключение —
// целиком выбрали свой же отдел. Срабатывает один раз: дальше выбор за пользователем
const onWorkerIdsChange = (value: SelectModelValue) => {
	const next = (Array.isArray(value) ? value : []) as number[]
	const groupIds = toggledGroupIds
	toggledGroupIds = null

	const selfId = currentWorkerId.value
	const prev = filters.value.WorkerIds ?? []
	const wasDefault = !workersTouched.value && prev.length === 1 && prev[0] === selfId
	workersTouched.value = true

	const shouldDropSelf = selfId != null
		&& wasDefault
		&& !groupIds?.includes(selfId)
		&& next.includes(selfId)
		&& next.some(id => id !== selfId)

	filters.value.WorkerIds = shouldDropSelf
		? next.filter(id => id !== selfId)
		: next
}

const focusMessage = () => {
	if (isMobile.value || isTablet.value) {
		return
	}
	$messageTextarea.value?.focus()
}

const buildCreatePayload = (): NewJob => {
	return {
		WorkerIds: filters.value.WorkerIds,
		NeedChecking: filters.value.NeedChecking,
		ToDoDate: filters.value.ToDoDate ? filters.value.ToDoDate.toISOString() : null,
		Message: trimTrailingLineBreaks(filters.value.Message),
		Tags: filters.value.Tags.map(t => t.Name),
	}
}

const onSave = async () => {
	try {
		isLoading.value = true
		if (props.type === 'create') {
			const created = await store.createJob(
				buildCreatePayload(),
				currentUser.value?.Id as number
			)
			if (created && created.length > 0) {
				for (const job of created) {
					if (pendingFiles.value.length > 0) {
						enqueueDeviceUpload(pendingFiles.value.slice(), job.Id)
					}
					if (pendingProjectFiles.value.length > 0) {
						await attachProjectFilesToJob(pendingProjectFiles.value.slice(), job.Id)
					}
				}
			}
			localStorage.removeItem('newJob')
		} else if (props.job) {
			await store.editJob({
				JobId: props.job.Id,
				WorkerId: filters.value.WorkerIds?.[0],
				NeedChecking: filters.value.NeedChecking,
				ToDoDate: filters.value.ToDoDate ? filters.value.ToDoDate.toISOString() : null,
				Message: trimTrailingLineBreaks(filters.value.Message),
				Tags: filters.value.Tags.map(t => t.Name),
			})
		}
		emit('on-close')
	} catch (e) {
		console.error(e)
	} finally {
		isLoading.value = false
	}
}

const onOpen = async () => {
	try {
		isLoading.value = true
		if (props.type === 'create') {
			const newJobFromLS = localStorage.getItem('newJob')
			if (newJobFromLS) {
				const parsed = JSON.parse(newJobFromLS) as Partial<JobFormState> & { WorkersTouched?: boolean }
				filters.value = {
					WorkerIds: parsed.WorkerIds ?? null,
					NeedChecking: parsed.NeedChecking ?? false,
					ToDoDate: parsed.ToDoDate
						? (typeof parsed.ToDoDate === 'string' ? new Date(parsed.ToDoDate) : parsed.ToDoDate)
						: null,
					Message: parsed.Message ?? '',
					Tags: (parsed.Tags ?? []) as TagWithFrequency[],
				}
				workersTouched.value = parsed.WorkersTouched ?? false
			} else {
				filters.value = {
					WorkerIds: null,
					NeedChecking: false,
					ToDoDate: null,
					Message: '',
					Tags: []
				}
				workersTouched.value = false
			}

			// Новую работу чаще всего ставят на себя — подставляем текущего пользователя,
			// пока исполнителей в черновике никто не трогал
			if (!workersTouched.value && !filters.value.WorkerIds?.length && currentWorkerId.value != null) {
				filters.value.WorkerIds = [currentWorkerId.value]
			}
		} else if (props.job) {
			filters.value = {
				WorkerIds: props.job.Worker ? [props.job.Worker.Id] : [],
				NeedChecking: props.job.NeedChecking,
				ToDoDate: props.job.ToDoDate ? new Date(props.job.ToDoDate) : null,
				Message: props.job.Message,
				Tags: (props.job.Tags ?? []) as unknown as TagWithFrequency[],
			}
		}

		nextTick(focusMessage)

		templates.value = await JobsApi.getJobsTemplates()
	} catch (e) {
		console.error(e)
	} finally {
		isLoading.value = false
	}
}

const toggleTemplates = () => {
	showTemplates.value = !showTemplates.value

	if (!showTemplates.value) {
		return
	}

	nextTick(() => $templatesSelect.value?.open())
}

const getTomorrow = (): Date => {
	const date = new Date()
	date.setDate(date.getDate() + 1)
	date.setHours(0, 0, 0, 0)

	return date
}

// Дата сама по себе необязательна, но с включённым «С отчетом» пустой остаться не может
const toggleNeedChecking = (value: boolean) => {
	filters.value.NeedChecking = value

	if (value && !filters.value.ToDoDate) {
		filters.value.ToDoDate = getTomorrow()
	}
}

const clearToDoDate = () => {
	filters.value.ToDoDate = filters.value.NeedChecking ? getTomorrow() : null
}

const createTemplate = async () => {
	const isSearchedTemplateExist = templates.value.find(template => template.Name === searchedTemplate.value)

	if (!isSearchedTemplateExist && searchedTemplate.value) {
		try {
			isLoading.value = true
			const result = await JobsApi.createJobsTemplate({
				Name: searchedTemplate.value,
				Text: filters.value.Message
			})

			templates.value.unshift({
				Id: result.Id,
				Name: searchedTemplate.value,
				Text: filters.value.Message
			})

			selectedTemplate.value = result.Id
		} catch (e) {
			console.error(e)
		} finally {
			isLoading.value = false
		}
	}
}

const openTemplateDeleteConfirm = (item: SelectItem, event: MouseEvent) => {
	const rect = (event.currentTarget as HTMLElement).getBoundingClientRect()
	const maxLeft = window.innerWidth - CONFIRM_WIDTH - VIEWPORT_GAP
	const openUp = rect.bottom + CONFIRM_HEIGHT + VIEWPORT_GAP > window.innerHeight

	templateConfirmStyle.value = {
		top: openUp
			? `${Math.max(VIEWPORT_GAP, rect.top - CONFIRM_HEIGHT - 4)}px`
			: `${rect.bottom + 4}px`,
		left: `${Math.max(VIEWPORT_GAP, Math.min(rect.right - CONFIRM_WIDTH, maxLeft))}px`
	}
	templateToDelete.value = { id: Number(item.id), label: item.label }

	window.addEventListener('scroll', closeTemplateConfirm, true)
}

const closeTemplateConfirm = () => {
	if (!templateToDelete.value) {
		return
	}

	templateToDelete.value = null
	window.removeEventListener('scroll', closeTemplateConfirm, true)
}

const confirmTemplateDelete = async () => {
	const target = templateToDelete.value
	if (!target || isTemplateDeleting.value) {
		return
	}

	try {
		isTemplateDeleting.value = true
		const isRemoved = await JobsApi.removeJobsTemplate(target.id)

		if (!isRemoved) {
			notify.danger(t('job_templates_title'), t('job_template_delete_error'))
			return
		}

		templates.value = templates.value.filter(template => template.Id !== target.id)
		if (selectedTemplate.value === target.id) {
			selectedTemplate.value = null
		}
		closeTemplateConfirm()
	} finally {
		isTemplateDeleting.value = false
	}
}

const isFormValid = computed(() =>
	!!filters.value.WorkerIds?.length
	&& !!filters.value.Message.trim()
	&& (!filters.value.NeedChecking || !!filters.value.ToDoDate)
)

watch([filters, workersTouched], () => {
	if (props.type === 'create') {
		localStorage.setItem('newJob', JSON.stringify({
			...filters.value,
			WorkersTouched: workersTouched.value,
		}))
	}
}, {
	deep: true,
})

watch(selectedTemplate, (newVal) => {
	if (newVal) {
		const searched = templates.value.find(template => template.Id === newVal)
		const text = searched?.Text || ''
		if (!text) return

		filters.value.Message = filters.value.Message
			? `${filters.value.Message} ${text}`
			: text
	}
})

onMounted(() => {
	onOpen()
})

onUnmounted(() => {
	closeTemplateConfirm()
})

const loaderStyle = computed(() => ({
	'background-color': 'rgba(255, 255, 255, 0.5)'
}))

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass({
			'create-edit-job': true,
			'create-edit-job--is-dragging-over': _unref(isDraggingOver),
			'create-edit-job--can-drop': _unref(canDrop) && _unref(isDraggingOver),
			'create-edit-job--cannot-drop': !_unref(canDrop) && _unref(isDraggingOver),
		}),
    onDragover: _cache[9] || (_cache[9] = 
//@ts-ignore
(...args) => (_unref(handleDragOver) && _unref(handleDragOver)(...args))),
    onDragenter: _cache[10] || (_cache[10] = 
//@ts-ignore
(...args) => (_unref(handleDragEnter) && _unref(handleDragEnter)(...args))),
    onDragleave: _cache[11] || (_cache[11] = 
//@ts-ignore
(...args) => (_unref(handleDragLeave) && _unref(handleDragLeave)(...args))),
    onDrop: _cache[12] || (_cache[12] = 
//@ts-ignore
(...args) => (_unref(handleDrop) && _unref(handleDrop)(...args)))
  }, [
    (isLoading.value)
      ? (_openBlock(), _createBlock(AppLoader, {
          key: 0,
          style: _normalizeStyle(loaderStyle.value)
        }, null, 8, ["style"]))
      : _createCommentVNode("", true),
    _createElementVNode("div", {
      class: _normalizeClass({
				'create-edit-job__drop-zone': true,
				'create-edit-job__drop-zone--can-drop': _unref(canDrop) && _unref(isDraggingOver),
				'create-edit-job__drop-zone--cannot-drop': !_unref(canDrop) && _unref(isDraggingOver),
			})
    }, null, 2),
    _createVNode(FileInfoModal, {
      ref_key: "$fileInfoModal",
      ref: $fileInfoModal,
      title: _ctx.$t('file_information_and_access')
    }, null, 8, ["title"]),
    (!_unref(isMobile))
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          _createElementVNode("div", _hoisted_2, _toDisplayString(_ctx.$t(props.type === 'create' ? 'job_create_title' : 'job_edit_title')), 1),
          _createVNode(AppTooltip, {
            text: _ctx.$t('close_label')
          }, {
            activator: _withCtx(() => [
              _createVNode(AppIcon, {
                class: "create-edit-job__header-close",
                name: "close",
                width: "20px",
                color: "#979797",
                "hover-color": "#499259",
                cursor: "pointer",
                onClick: _cache[0] || (_cache[0] = ($event: any) => (emit('on-close')))
              })
            ]),
            _: 1
          }, 8, ["text"])
        ]))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_3, [
      _createVNode(AppSelect, {
        "model-value": filters.value.WorkerIds,
        placeholder: _ctx.$t('job_workers_placeholder'),
        items: props.workers,
        "pinned-items": props.pinnedWorkers ?? [],
        disabled: props.type === 'edit',
        "selected-count-key": "job_workers_count",
        "description-key": "status",
        "description-tooltip-max-width": "320px",
        searchable: "",
        multiple: "",
        "onUpdate:modelValue": onWorkerIdsChange,
        onToggleGroup: onWorkersGroupToggle
      }, null, 8, ["model-value", "placeholder", "items", "pinned-items", "disabled"]),
      _createElementVNode("div", _hoisted_4, [
        _createElementVNode("div", _hoisted_5, [
          _createVNode(AppCheckbox, {
            value: filters.value.NeedChecking,
            onOnInput: toggleNeedChecking
          }, null, 8, ["value"]),
          _createElementVNode("div", _hoisted_6, _toDisplayString(_ctx.$t('job_with_report')), 1),
          _createVNode(AppDateString, {
            modelValue: filters.value.ToDoDate,
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((filters.value.ToDoDate) = $event)),
            "min-date": new Date()
          }, null, 8, ["modelValue", "min-date"]),
          (filters.value.ToDoDate)
            ? (_openBlock(), _createBlock(AppTooltip, {
                key: 0,
                text: _ctx.$t('date_reset')
              }, {
                activator: _withCtx(() => [
                  _createVNode(AppIcon, {
                    class: "create-edit-job__body-options-date-clear",
                    name: "close-bold",
                    width: "12px",
                    color: "#D2D2D2",
                    "hover-color": "#BA4C4C",
                    cursor: "pointer",
                    onClick: clearToDoDate
                  })
                ]),
                _: 1
              }, 8, ["text"]))
            : _createCommentVNode("", true)
        ]),
        _createVNode(AppTooltip, {
          class: "create-edit-job__body-options-templates",
          text: _ctx.$t('job_templates_title')
        }, {
          activator: _withCtx(() => [
            _createVNode(AppIcon, {
              name: "file-text",
              width: "18px",
              color: showTemplates.value ? '#3F884F' : '#979797',
              "hover-color": "#499259",
              cursor: "pointer",
              onClick: toggleTemplates
            }, null, 8, ["color"])
          ]),
          _: 1
        }, 8, ["text"])
      ]),
      _createVNode(AppTextarea, {
        ref_key: "$messageTextarea",
        ref: $messageTextarea,
        class: "create-edit-job__textarea",
        modelValue: filters.value.Message,
        "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((filters.value.Message) = $event)),
        height: "100%",
        placeholder: _ctx.$t('job_message_placeholder')
      }, null, 8, ["modelValue", "placeholder"]),
      (showTemplates.value)
        ? (_openBlock(), _createBlock(AppSelect, {
            key: 0,
            ref_key: "$templatesSelect",
            ref: $templatesSelect,
            class: "create-edit-job__templates",
            modelValue: selectedTemplate.value,
            "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((selectedTemplate).value = $event)),
            placeholder: _ctx.$t('job_template_placeholder'),
            "search-empty-text": _ctx.$t('job_template_not_found'),
            items: mappedTemplates.value,
            searchable: "",
            clearable: "",
            onSearch: _cache[7] || (_cache[7] = ($event: any) => (searchedTemplate.value = $event)),
            onClose: closeTemplateConfirm
          }, {
            "append-icon": _withCtx(() => [
              (searchedTemplate.value)
                ? (_openBlock(), _createBlock(AppTooltip, {
                    key: 0,
                    text: _ctx.$t('job_template_add')
                  }, {
                    activator: _withCtx(() => [
                      _createVNode(AppIcon, {
                        name: "plus",
                        width: "16px",
                        color: "#979797",
                        "hover-color": "#499259",
                        cursor: "pointer",
                        onClick: createTemplate
                      })
                    ]),
                    _: 1
                  }, 8, ["text"]))
                : _createCommentVNode("", true)
            ]),
            "option-append": _withCtx(({ item, hovered }) => [
              (hovered || templateToDelete.value?.id === item.id)
                ? (_openBlock(), _createBlock(AppTooltip, {
                    key: 0,
                    text: _ctx.$t('job_template_remove')
                  }, {
                    activator: _withCtx(() => [
                      _createVNode(AppIcon, {
                        name: "delete-outline",
                        width: "16px",
                        color: "#979797",
                        "hover-color": "#BA4C4C",
                        cursor: "pointer",
                        onClick: _withModifiers(($event: any) => (openTemplateDeleteConfirm(item, $event)), ["stop"])
                      }, null, 8, ["onClick"])
                    ]),
                    _: 2
                  }, 1032, ["text"]))
                : _createCommentVNode("", true),
              (templateToDelete.value?.id === item.id)
                ? (_openBlock(), _createElementBlock("div", {
                    key: 1,
                    class: "create-edit-job__template-confirm",
                    style: _normalizeStyle(templateConfirmStyle.value),
                    onClick: _cache[3] || (_cache[3] = _withModifiers(() => {}, ["stop"])),
                    onMousedown: _cache[4] || (_cache[4] = _withModifiers(() => {}, ["stop"])),
                    onKeydown: _cache[5] || (_cache[5] = _withModifiers(() => {}, ["stop"]))
                  }, [
                    _createVNode(ConfirmActionDropdown, {
                      title: _ctx.$t('job_template_delete_title'),
                      description: _ctx.$t('job_template_delete_question', { name: templateToDelete.value.label }),
                      "confirm-label": _ctx.$t('remove'),
                      onCancel: closeTemplateConfirm,
                      onConfirm: confirmTemplateDelete
                    }, null, 8, ["title", "description", "confirm-label"])
                  ], 36))
                : _createCommentVNode("", true)
            ]),
            _: 1
          }, 8, ["modelValue", "placeholder", "search-empty-text", "items"]))
        : _createCommentVNode("", true),
      _createElementVNode("div", {
        class: _normalizeClass({
					'create-edit-job__body-attachments': true,
					'create-edit-job__body-attachments--have-files': hasAnyFiles.value,
				})
      }, [
        _createElementVNode("div", _hoisted_7, [
          _createElementVNode("input", {
            ref_key: "$uploadFile",
            ref: $uploadFile,
            type: "file",
            class: "create-edit-job__upload-input",
            multiple: "",
            onChange: handleFileInput
          }, null, 544),
          _createVNode(AppTooltip, {
            text: _ctx.$t('file_upload')
          }, {
            activator: _withCtx(() => [
              _createVNode(AppIcon, {
                name: "clip",
                width: "18px",
                color: "#979797",
                "hover-color": "#499259",
                cursor: "pointer",
                onClick: triggerFileInput
              })
            ]),
            _: 1
          }, 8, ["text"]),
          (hasAnyFiles.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(existingFiles.value, (file) => {
                  return (_openBlock(), _createElementBlock("div", {
                    key: `exist-${file.Id}`,
                    class: "create-edit-job__body-files-item"
                  }, [
                    _createVNode(AppTooltip, {
                      text: _ctx.$t('information')
                    }, {
                      activator: _withCtx(() => [
                        _createVNode(AppIcon, {
                          class: "create-edit-job__body-files-item-icon",
                          name: "info-circle-outline",
                          width: "18px",
                          color: "#979797",
                          "hover-color": "#499259",
                          cursor: "pointer",
                          onClick: ($event: any) => (showFileInfo(file))
                        }, null, 8, ["onClick"])
                      ]),
                      _: 2
                    }, 1032, ["text"]),
                    _createVNode(AppTooltip, {
                      class: "create-edit-job__body-files-item-name-tooltip",
                      text: _ctx.$t('file_download')
                    }, {
                      activator: _withCtx(() => [
                        _createElementVNode("div", {
                          class: "create-edit-job__body-files-item-name",
                          onClick: ($event: any) => (downloadFile(file.Id))
                        }, _toDisplayString(file.Name), 9, _hoisted_9)
                      ]),
                      _: 2
                    }, 1032, ["text"]),
                    _createVNode(AppTooltip, {
                      text: _ctx.$t('file_detach')
                    }, {
                      activator: _withCtx(() => [
                        _createVNode(AppIcon, {
                          class: "create-edit-job__body-files-item-delete",
                          name: "close-bold",
                          width: "12px",
                          color: "#D2D2D2",
                          "hover-color": "#BA4C4C",
                          cursor: "pointer",
                          onClick: ($event: any) => (detachExistingFile(file))
                        }, null, 8, ["onClick"])
                      ]),
                      _: 2
                    }, 1032, ["text"]),
                    (fileLocationTarget.value)
                      ? (_openBlock(), _createBlock(AppTooltip, {
                          key: 0,
                          text: _ctx.$t('file_go_to_location')
                        }, {
                          activator: _withCtx(() => [
                            _createVNode(AppIcon, {
                              class: "create-edit-job__body-files-item-icon",
                              name: "arrow-45deg",
                              width: "18px",
                              color: "#979797",
                              "hover-color": "#499259",
                              cursor: "pointer",
                              onClick: goToFileLocation
                            })
                          ]),
                          _: 1
                        }, 8, ["text"]))
                      : _createCommentVNode("", true)
                  ]))
                }), 128)),
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(pendingProjectFiles.value, (file, index) => {
                  return (_openBlock(), _createElementBlock("div", {
                    key: `pp-${index}-${file.Id}`,
                    class: "create-edit-job__body-files-item"
                  }, [
                    _createElementVNode("div", _hoisted_10, _toDisplayString(file.Name), 1),
                    _createVNode(AppTooltip, {
                      text: _ctx.$t('file_remove_from_list')
                    }, {
                      activator: _withCtx(() => [
                        _createVNode(AppIcon, {
                          class: "create-edit-job__body-files-item-delete",
                          name: "close-bold",
                          width: "12px",
                          color: "#D2D2D2",
                          "hover-color": "#BA4C4C",
                          cursor: "pointer",
                          onClick: ($event: any) => (removePendingProjectFile(index))
                        }, null, 8, ["onClick"])
                      ]),
                      _: 2
                    }, 1032, ["text"])
                  ]))
                }), 128)),
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(pendingFiles.value, (file, index) => {
                  return (_openBlock(), _createElementBlock("div", {
                    key: `pf-${index}-${file.name}`,
                    class: "create-edit-job__body-files-item"
                  }, [
                    _createElementVNode("div", _hoisted_11, _toDisplayString(file.name), 1),
                    _createVNode(AppTooltip, {
                      text: _ctx.$t('file_remove_from_list')
                    }, {
                      activator: _withCtx(() => [
                        _createVNode(AppIcon, {
                          class: "create-edit-job__body-files-item-delete",
                          name: "close-bold",
                          width: "12px",
                          color: "#D2D2D2",
                          "hover-color": "#BA4C4C",
                          cursor: "pointer",
                          onClick: ($event: any) => (removePendingFile(index))
                        }, null, 8, ["onClick"])
                      ]),
                      _: 2
                    }, 1032, ["text"])
                  ]))
                }), 128))
              ]))
            : _createCommentVNode("", true)
        ]),
        _createElementVNode("div", _hoisted_12, [
          _createVNode(AppResizableDropdown, {
            ref_key: "$tagsDropdown",
            ref: $tagsDropdown,
            position: "right",
            "close-by-click-outside": true,
            "max-width": 640,
            "max-height": 460,
            "mobile-title": _ctx.$t('choose_tags')
          }, {
            activator: _withCtx(() => [
              _createVNode(AppTooltip, {
                text: _ctx.$t('file_add_tag')
              }, {
                activator: _withCtx(() => [
                  _createVNode(AppIcon, {
                    class: "create-edit-job__body-tags-add",
                    name: "hash",
                    width: "18px",
                    color: "#979797",
                    "hover-color": "#499259",
                    cursor: "pointer"
                  })
                ]),
                _: 1
              }, 8, ["text"])
            ]),
            default: _withCtx(() => [
              _createVNode(AppTagSelector, {
                "model-value": filters.value.Tags,
                "entity-type": _unref(TagEntityType).Job,
                mode: "select",
                "allow-create": "",
                "onUpdate:modelValue": onTagSelectorChange,
                onCreateTag: onCreateTag,
                onClose: _cache[8] || (_cache[8] = ($event: any) => ($tagsDropdown.value?.close()))
              }, null, 8, ["model-value", "entity-type"])
            ]),
            _: 1
          }, 8, ["mobile-title"]),
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filters.value.Tags, (tag) => {
            return (_openBlock(), _createElementBlock("span", {
              class: "create-edit-job__body-tag",
              key: tag.Id || tag.Name
            }, [
              _createTextVNode(_toDisplayString(tag.Name.toLowerCase().trim()) + " ", 1),
              _createVNode(AppTooltip, {
                text: _ctx.$t('ui_tag_detach')
              }, {
                activator: _withCtx(() => [
                  _createVNode(AppIcon, {
                    class: "create-edit-job__body-tag-delete",
                    name: "close-bold",
                    width: "12px",
                    color: "#D2D2D2",
                    "hover-color": "#BA4C4C",
                    cursor: "pointer",
                    onClick: ($event: any) => (removeTag(tag))
                  }, null, 8, ["onClick"])
                ]),
                _: 2
              }, 1032, ["text"])
            ]))
          }), 128))
        ])
      ], 2)
    ]),
    _cache[13] || (_cache[13] = _createElementVNode("div", { class: "create-edit-job__divider" }, null, -1)),
    _createElementVNode("div", _hoisted_13, [
      _createVNode(AppButton, {
        label: _ctx.$t('save'),
        onClick: onSave,
        disabled: isLoading.value || !isFormValid.value
      }, null, 8, ["label", "disabled"])
    ])
  ], 34))
}
}

})